import { Therapist } from "../../../therapist";
import apiClient from "../../../../shared/infrastructure/api/client";

export interface SignUpDTO {
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  country?: string;
  language?: string;
  patient_term?: string;
  invitation_token?: string;
  /**
   * A centre's invitation, which is a different thing from `invitation_token`
   * above: one decides what the account can pay for, the other which
   * organisation it belongs to.
   */
  center_invitation_token?: string;
}

export interface InvitationValidateResponseDTO {
  valid: boolean;
  /** Which sign-up the code opens. Only present for a live code. */
  kind?: "therapist" | "center" | null;
}

export interface SignInDTO {
  email: string;
  password?: string;
}

/** A code from the authenticator app; the Firebase session names the account. */
export interface MfaCodeDTO {
  totp_code: string;
}

export interface LoginResponseDTO {
  status: "SUCCESS" | "MFA_REQUIRED";
  therapist?: Therapist;
}

export interface MfaSetupResponseDTO {
  secret: string;
  qr_code_base64: string;
}

export interface RegisterResponseDTO {
  therapist: Therapist;
  mfa: MfaSetupResponseDTO;
}

export interface GoogleLoginResponseDTO {
  therapist: Therapist;
  is_new_user: boolean;
  /** The account has MFA on: finish with `mfaVerify`. */
  mfa_required?: boolean;
}

export const AuthAPI = {
  signUp: (data: SignUpDTO) => apiClient.post<RegisterResponseDTO>("/auth/register", data),
  signIn: (data: SignInDTO) => apiClient.post<LoginResponseDTO>("/auth/login", data),
  mfaVerify: (data: MfaCodeDTO) => apiClient.post<Therapist>("/auth/mfa/verify", data),
  mfaSetup: () => apiClient.post<MfaSetupResponseDTO>("/auth/mfa/setup"),
  mfaEnable: (data: MfaCodeDTO) => apiClient.post<Therapist>("/auth/mfa/enable", data),
  mfaDisable: (data: { password: string }) => apiClient.post("/auth/mfa/disable", data),
  sendVerificationEmail: (continueUrl: string) =>
    apiClient.post("/auth/verification-email/send", { continue_url: continueUrl }),
  sendPasswordResetEmail: (email: string, language?: string) =>
    apiClient.post("/auth/reset-password/send", { email, language }),
  sendWelcomeEmail: () => apiClient.post("/auth/welcome-email/send"),
  googleSignIn: (invitation_token?: string) =>
    apiClient.post<GoogleLoginResponseDTO>("/auth/google", { invitation_token }),
  validateInvitation: (token: string) =>
    apiClient.get<InvitationValidateResponseDTO>(`/invitations/${token}`),
};
